import React from 'react';

import { ApSimpleDropdown } from '@alixpartners/ui-components';
import { ChartOptionsCollapsible } from 'components/ChartOptionsCollapsible';
import {
  Wrapper,
  Subtitle,
  Title,
  Divider,
  OptionsWrapper,
  CheckboxWrapper,
  StyledCheckbox,
} from './ChartOptions.styles';
import { EXPORT_OPTIONS } from 'utils/constants';
import {
  SPEND_TYPE_OPTIONS,
  COMPARE_VALUE_OPTIONS,
  BENCHMARK_METRIC_SPEND_OPTIONS,
  BENCHMARK_METRIC_HEADCOUNT_OPTIONS,
  COMPARE_CATEGORY_SPEND_OPTIONS,
  COMPARE_CATEGORY_HEADCOUNT_OPTIONS,
  COMPARE_BY_OPTIONS,
  COMPARE_TYPE_OPTIONS,
  CompareValueEnum,
  CompareByEnum,
} from '../utils';
import { IConfig } from '../types';

interface IDropdownOption {
  label: string;
  value: string;
}

interface IProps {
  title: string;
  subtitle?: string;
  config: IConfig;
  isExportDisabled?: boolean;
  onConfigChange(updates: Partial<IConfig>): void;
  onExport(value: string): void;
  hasSalaryPermission?: boolean;
}

export const ChartOptions: React.FC<IProps> = ({
  title,
  subtitle,
  config,
  isExportDisabled,
  onConfigChange,
  onExport,
  hasSalaryPermission,
}) => {
  const handleDropdownChange =
    (name: string) => (option: IDropdownOption | IDropdownOption[]) => {
      const value = (option as IDropdownOption).value;
      let updates = {
        [name]: value,
      } as Partial<IConfig>;

      if (name === 'compareValue') {
        if (value === CompareValueEnum.HEADCOUNT) {
          updates = {
            ...updates,
            compareCategory: COMPARE_CATEGORY_HEADCOUNT_OPTIONS[0].value,
            benchmarkMetric: BENCHMARK_METRIC_HEADCOUNT_OPTIONS[0].value,
          };
        } else {
          updates = {
            ...updates,
            compareCategory: COMPARE_CATEGORY_SPEND_OPTIONS[0].value,
            benchmarkMetric: BENCHMARK_METRIC_SPEND_OPTIONS[0].value,
          };
        }
      }

      onConfigChange(updates);
    };

  const handleCheckboxChange = (e: any) => {
    const name = e.target.name;
    const checked = e.target.checked;

    onConfigChange({ [name]: checked });
  };

  const handleExport = (item: IDropdownOption | IDropdownOption[]) => {
    onExport((item as IDropdownOption).value);
  };

  const isHeadcount = () => config.compareValue === CompareValueEnum.HEADCOUNT;

  const getDropdownAvailableOptions = (name: string): IDropdownOption[] => {
    const optionsByName = {
      spendType: SPEND_TYPE_OPTIONS,
      compareBy: COMPARE_BY_OPTIONS.map((item) => ({
        ...item,
        disabled:
          !hasSalaryPermission &&
          item.value === CompareByEnum.PERCENT_OF_REVENUE,
      })),
      compareValue: COMPARE_VALUE_OPTIONS,
      compareType: COMPARE_TYPE_OPTIONS,
      compareCategory: !isHeadcount()
        ? COMPARE_CATEGORY_SPEND_OPTIONS
        : COMPARE_CATEGORY_HEADCOUNT_OPTIONS,
      benchmarkMetric: !isHeadcount()
        ? BENCHMARK_METRIC_SPEND_OPTIONS
        : BENCHMARK_METRIC_HEADCOUNT_OPTIONS,
    } as any;

    return optionsByName[name];
  };

  const getSelectedOption = (name: string): IDropdownOption => {
    const dropdownOptions = getDropdownAvailableOptions(name);

    return (
      dropdownOptions.find(
        (item) => item.value === config[name as keyof IConfig]
      ) || { label: '', value: '' }
    );
  };

  const renderHeader = () => (
    <Wrapper>
      <Title>
        {title}
        {subtitle && (
          <Subtitle>
            <span>{subtitle}</span>
          </Subtitle>
        )}
      </Title>
    </Wrapper>
  );

  return (
    <ChartOptionsCollapsible header={renderHeader}>
      <Wrapper>
        <OptionsWrapper>
          <ApSimpleDropdown
            onChange={handleDropdownChange('spendType')}
            options={getDropdownAvailableOptions('spendType')}
            selected={getSelectedOption('spendType')}
          />
          <ApSimpleDropdown
            onChange={handleDropdownChange('compareValue')}
            options={getDropdownAvailableOptions('compareValue')}
            selected={getSelectedOption('compareValue')}
          />
          <ApSimpleDropdown
            onChange={handleDropdownChange('benchmarkMetric')}
            options={getDropdownAvailableOptions('benchmarkMetric')}
            selected={getSelectedOption('benchmarkMetric')}
            disabled={
              getDropdownAvailableOptions('benchmarkMetric').length <= 1
            }
          />
          <ApSimpleDropdown
            onChange={handleDropdownChange('compareCategory')}
            options={getDropdownAvailableOptions('compareCategory')}
            selected={getSelectedOption('compareCategory')}
            disabled={
              getDropdownAvailableOptions('compareCategory').length <= 1
            }
          />
          {!isHeadcount() && (
            <CheckboxWrapper>
              <StyledCheckbox
                id="showTotal"
                name="showTotal"
                checked={config.showTotal}
                onChange={handleCheckboxChange}
              >
                Show total
              </StyledCheckbox>
            </CheckboxWrapper>
          )}
          <Divider />
          {!isHeadcount() && (
            <ApSimpleDropdown
              onChange={handleDropdownChange('compareBy')}
              options={getDropdownAvailableOptions('compareBy')}
              selected={getSelectedOption('compareBy')}
            />
          )}
          <ApSimpleDropdown
            onChange={handleDropdownChange('compareType')}
            options={getDropdownAvailableOptions('compareType')}
            selected={getSelectedOption('compareType')}
          />
          <Divider />
          {!isHeadcount() && (
            <React.Fragment>
              <CheckboxWrapper>
                <StyledCheckbox
                  id="showPeerMedian"
                  name="showPeerMedian"
                  checked={config.showPeerMedian}
                  onChange={handleCheckboxChange}
                >
                  Peer median
                </StyledCheckbox>
              </CheckboxWrapper>
              <CheckboxWrapper disabled={isHeadcount()}>
                <StyledCheckbox
                  id="showTopQuartile"
                  name="showTopQuartile"
                  checked={config.showTopQuartile}
                  onChange={handleCheckboxChange}
                  disabled={isHeadcount()}
                >
                  Top quartile
                </StyledCheckbox>
              </CheckboxWrapper>
              <Divider />
            </React.Fragment>
          )}
          <ApSimpleDropdown
            iconName="outline_save_alt"
            onChange={handleExport}
            options={EXPORT_OPTIONS}
            label="Export"
            disabled={isExportDisabled}
          />
        </OptionsWrapper>
      </Wrapper>
    </ChartOptionsCollapsible>
  );
};
